import React, { useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import useTranslation from "../../../../shared/hooks/useTranslation";
import dayjs from "dayjs";
import { formatUiDate, formatUiDateRange } from "../../../../shared/utils/dateFormat";

interface ScheduleHeaderProps {
  calendarRef: React.RefObject<FullCalendar>;
  currentView: string;
  currentDate: Date;
  onViewChange: (view: string) => void;
  /** When true, hide the Week/Month/Day toggle pills (phone layout) */
  hideViewToggle?: boolean;
  /** When true, use compact layout for small screens */
  compact?: boolean;
}

const viewOptions = [
  { key: "timeGridDay", labelKey: "calendar.day" },
  { key: "timeGridWeek", labelKey: "calendar.week" },
  { key: "dayGridMonth", labelKey: "calendar.month" },
];

const formatDateRange = (date: Date, view: string): string => {
  const d = dayjs(date);
  if (view === "dayGridMonth") {
    return d.format("MMMM YYYY");
  }
  if (view === "timeGridDay") {
    return formatUiDate(d, "weekdayDate");
  }
  // Week view: show range with em dash, in the UI language's date order
  const start = d.startOf("week");
  return formatUiDateRange(start, start.add(6, "day"), " \u2014 ");
};

const ScheduleHeader: React.FC<ScheduleHeaderProps> = ({
  calendarRef,
  currentView,
  currentDate,
  onViewChange,
  hideViewToggle = false,
  compact = false,
}) => {
  const { translate } = useTranslation("operations");

  const handlePrev = useCallback(() => {
    calendarRef.current?.getApi().prev();
  }, [calendarRef]);

  const handleNext = useCallback(() => {
    calendarRef.current?.getApi().next();
  }, [calendarRef]);

  const handleToday = useCallback(() => {
    calendarRef.current?.getApi().today();
  }, [calendarRef]);

  const handleViewClick = useCallback(
    (viewKey: string) => {
      onViewChange(viewKey);
      calendarRef.current?.getApi().changeView(viewKey);
    },
    [calendarRef, onViewChange]
  );

  return (
    <div className={`w-full ${compact ? "px-4 py-2.5" : "px-8 py-3.5"} flex items-center justify-between flex-wrap gap-3`}>
      {/* Left: Title + View toggle pills */}
      <div className="flex items-center gap-4">
        <span
          className={`${compact ? "text-base" : "text-xl"} font-bold text-mid-charcoal`}
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {translate("calendar.title")}
        </span>

        {/* #5-#9: View toggle pills — Week, Month, Day order (hidden on phone) */}
        {!hideViewToggle && (
          <div className="flex gap-2">
            {viewOptions.map(({ key, labelKey }) => (
              <button
                key={key}
                onClick={() => handleViewClick(key)}
                className={`px-3.5 py-1.5 rounded-pill text-xs font-medium transition-all ${
                  currentView === key
                    ? "bg-mid-charcoal text-[#F7F5F0] font-semibold"
                    : "bg-mid-board text-mid-body"
                }`}
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                {translate(labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right: Date nav arrows + date label + Today button */}
      <div className="flex items-center gap-2">
        {/* Prev arrow — 44px touch target on phone (compact) */}
        <button
          onClick={handlePrev}
          className={`${compact ? "min-w-[44px] min-h-[44px]" : "w-7 h-7"} rounded-[6px] bg-white border border-mid-border flex items-center justify-center text-mid-muted hover:text-mid-charcoal transition-colors`}
          aria-label={translate("common:buttons.previous")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Date label — Sora font, bold on phone for readability */}
        <span
          className={`${compact ? "text-[15px] font-bold" : "text-sm font-semibold"} text-mid-charcoal ${compact ? "min-w-[140px]" : "min-w-[180px]"} text-center`}
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {formatDateRange(currentDate, currentView)}
        </span>

        {/* Next arrow — 44px touch target on phone (compact) */}
        <button
          onClick={handleNext}
          className={`${compact ? "min-w-[44px] min-h-[44px]" : "w-7 h-7"} rounded-[6px] bg-white border border-mid-border flex items-center justify-center text-mid-muted hover:text-mid-charcoal transition-colors`}
          aria-label={translate("common:buttons.next")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Today button */}
        <button
          onClick={handleToday}
          className={`${compact ? "px-2.5 py-1 min-h-[44px]" : "px-3.5 py-1.5"} rounded-[6px] bg-white border border-mid-border text-xs font-medium text-mid-charcoal hover:border-mid-charcoal transition-colors ml-1`}
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {translate("calendar.today")}
        </button>
      </div>
    </div>
  );
};

export default ScheduleHeader;
